import { useEffect } from 'react';
import { create } from 'zustand';
import {
  getChatRooms, getChatMessages, sendChatMessage, markChatRead,
  type ChatRoom, type ChatMessage,
} from '@/lib/api/chat';
import { startChatHub, type ChatPush } from '@/lib/chatHub';

/**
 * Staff messaging state, lifted out of the old /chat page so it lives app-wide: the assistant
 * panel renders it, and the unread badge stays live on every page (the hub is subscribed once
 * via useChatRealtime, mirroring how NotificationBell owns the notifications hub).
 */
interface ChatState {
  rooms: ChatRoom[];
  activeRoomId: string | null;
  messages: ChatMessage[];
  loadingRooms: boolean;
  loadingMessages: boolean;
  sending: boolean;
  loadRooms: () => Promise<ChatRoom[]>;
  openRoom: (roomId: string) => Promise<void>;
  closeRoom: () => void;
  send: (body: string) => Promise<void>;
  receivePush: (push: ChatPush) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  rooms: [],
  activeRoomId: null,
  messages: [],
  loadingRooms: false,
  loadingMessages: false,
  sending: false,

  loadRooms: async () => {
    set({ loadingRooms: true });
    try {
      const rooms = await getChatRooms();
      set({ rooms });
      return rooms;
    } catch {
      return get().rooms;
    } finally {
      set({ loadingRooms: false });
    }
  },

  openRoom: async (roomId) => {
    set({ activeRoomId: roomId, messages: [], loadingMessages: true });
    try {
      const messages = await getChatMessages(roomId);
      // Guard against a fast room switch resolving out of order.
      if (get().activeRoomId === roomId) set({ messages });
    } finally {
      set({ loadingMessages: false });
    }
    markChatRead(roomId).catch(() => undefined);
    set({ rooms: get().rooms.map((r) => (r.id === roomId ? { ...r, unreadCount: 0 } : r)) });
  },

  closeRoom: () => set({ activeRoomId: null, messages: [] }),

  send: async (body) => {
    const roomId = get().activeRoomId;
    if (!roomId || !body.trim()) return;
    set({ sending: true });
    try {
      const msg = await sendChatMessage(roomId, body.trim());
      set({
        messages: [...get().messages, msg],
        rooms: get().rooms.map((r) => (r.id === roomId
          ? { ...r, lastMessagePreview: msg.body, lastMessageAt: msg.createdAt } : r)),
      });
    } finally {
      set({ sending: false });
    }
  },

  // Live push: append to the open thread (and clear its unread), else bump that room's unread.
  receivePush: (push) => {
    const { activeRoomId, messages, rooms } = get();
    const preview = { lastMessagePreview: push.message.body, lastMessageAt: push.message.createdAt };
    if (push.roomId === activeRoomId) {
      set({
        messages: [...messages, push.message],
        rooms: rooms.map((r) => (r.id === push.roomId ? { ...r, ...preview } : r)),
      });
      markChatRead(push.roomId).catch(() => undefined);
    } else {
      set({
        rooms: rooms.map((r) => (r.id === push.roomId
          ? { ...r, ...preview, unreadCount: r.unreadCount + 1 } : r)),
      });
    }
  },
}));

/** Total unread across rooms — drives the assistant launcher badge. */
export const useUnreadTotal = () =>
  useChatStore((s) => s.rooms.reduce((n, r) => n + r.unreadCount, 0));

/**
 * Subscribe to the chat hub once for the whole app, so unread stays live regardless of the page
 * or whether the assistant panel is open. Mount from AppLayout.
 */
export const useChatRealtime = () => {
  const loadRooms = useChatStore((s) => s.loadRooms);
  useEffect(() => {
    void loadRooms();
    const stop = startChatHub((push) => useChatStore.getState().receivePush(push));
    return stop;
  }, [loadRooms]);
};
